/**
 * @file AuthForm.jsx
 * @description Authentication gateway for SimpleCode. Features a secure, premium Google OAuth 
 *              entry point within a light-themed Bento UI.
 * @module components/AuthForm
 */
import React, { useState, useEffect } from "react";
import {
    FiHome,
    FiShield,
    FiLock,
    FiArrowRight,
    FiAlertCircle,
    FiMail
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component AuthForm
 * @description Main authentication component, primarily handling Google OAuth via Supabase.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered Authentication Form page.
 */
const AuthForm = ({ navigate }) => {
    // ── Component State ──
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { signInWithGoogle, isAuthenticated } = useAuth();

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    // ── Navigation Links (Synchronized across the platform) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | Sign - In";
        // Clear error after timeout
        if (error) {
            const timer = setTimeout(() => setError(""), 5000);
            return () => {
                clearTimeout(timer);
                document.title = "SimpleCode";
            };
        }
        return () => { document.title = "SimpleCode"; };
    }, [error]);

    /* ══════════════════════════════════════════════════════════════
     * EVENT HANDLERS
     * ══════════════════════════════════════════════════════════════ */

    /**
     * @function handleGoogleLogin
     * @description Initiates the Google OAuth flow via AuthContext.
     */
    const handleGoogleLogin = async () => {
        setLoading(true);
        setError("");
        try {
            await signInWithGoogle();
            // Redirect is handled by Supabase/AuthContext
        } catch (err) {
            console.error('Google login error:', err);
            setError("Failed to start Google login. Please try again.");
            setLoading(false);
        }
    };

    /* ══════════════════════════════════════════════════════════════
     * SUB-COMPONENTS
     * ══════════════════════════════════════════════════════════════ */

    /**
     * @component GoogleIcon
     * @description Renders the official Google 'G' logo SVG.
     * @returns {JSX.Element}
     */
    const GoogleIcon = () => (
        <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    );

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-14 flex flex-col items-center mt-8">

                {/* ── Auth Header ── */}
                <section className="w-full bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mt-12">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-8">
                        <div className="flex flex-col sm:items-center sm:justify-center gap-2">
                            <h1 className="text-center text-3xl font-bold text-foreground decoration-dotted decoration-foreground underline-offset-8">
                                Welcome to <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent decoration-dotted decoration-emerald-600 ml-1">SimpleCode</span>
                            </h1>
                            <p className="text-muted-foreground text-sm font-medium text-center">Accelerate Your Tech Journey to Success</p>
                        </div>
                    </div>
                </section>

                <div className="w-full max-w-md px-4 pb-20">
                    {/* ── Auth Bento Card ── */}
                    <div className="bg-emerald-600 rounded overflow-hidden relative p-8 shadow-sm">
                        <div className="relative z-10">
                            {/* Heading */}
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-semibold text-white mb-2">Secure Sign-in</h2>
                                {/* <p className="text-white text-sm">Access your personalized learning dashboard</p> */}
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded flex items-center space-x-3 animate-fade-in">
                                    <FiAlertCircle className="text-xl text-red-500 flex-shrink-0" />
                                    <p className="text-red-700 text-xs font-medium text-left">{error}</p>
                                </div>
                            )}

                            {/* Google Sign-in Button */}
                            <button
                                onClick={handleGoogleLogin}
                                disabled={loading}
                                className="w-full group relative overflow-hidden py-4 px-6 bg-white border border-gray-100 rounded shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center space-x-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <div className="flex items-center space-x-3">
                                        <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-sm font-bold text-emerald-600 uppercase tracking-widest">Sign-in initiated...</span>
                                    </div>
                                ) : (
                                    <>
                                        <GoogleIcon />
                                        <span className="text-xs md:text-sm font-bold text-gray-700 group-hover:text-emerald-700 transition-colors uppercase md:tracking-widest">Continue with Google</span>
                                        <FiArrowRight className="w-4 h-4 text-emerald-600 transform group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>

                            {/* Divider & Info */}
                            <div className="mt-8 pt-6 border-t border-gray-50 text-center">
                                <p className="text-white text-xs leading-relaxed max-w-[260px] mx-auto">
                                    We use Google for enterprise-grade security. <br></br> No passwords required.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* ── Compliance Footer ── */}
                    <div className="mt-8 text-center text-xs text-muted-foreground px-4">
                        <p className="leading-relaxed">
                            By continuing, you agree to SimpleCode's{" "} <br></br>
                            <button 
                                onClick={() => navigate('/terms-of-service')} 
                                className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors hover:underline"
                            >
                                Terms of Service
                            </button>{" "}
                            and{" "}
                            <button 
                                onClick={() => navigate('/privacy-policy')}
                                className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors hover:underline"
                            >
                                Privacy Policy
                            </button>
                        </p>
                    </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

export default AuthForm;